import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBlogs } from '../../Utils/api';
import { HOME_SECTIONS } from '../../i18n/sectionCopy';
import { useLocalizedStatic } from '../../i18n/useLocalizedStatic';
import { useLanguage } from '../../i18n/LanguageContext';
import { mapBlogListItem } from '../../Utils/blogHelpers';
import { destroyOwlCarousel, initOwlWhenReady } from '../../Utils/carouselHelpers';

const img = (path) => `${process.env.PUBLIC_URL}/assets/images/${path}`;

const OWL_OPTIONS = {
  loop: false,
  items: 3,
  margin: 30,
  autoplay: true,
  autoplayHoverPause: true,
  autoplayTimeout: 5000,
  smartSpeed: 800,
  dots: false,
  nav: false,
  responsive: {
    0: { items: 1 },
    768: { items: 2 },
    992: { items: 2 },
    1200: { items: 3 },
  },
};

const HomeBlogSection = () => {
  const { locale, revision } = useLanguage();
  const copy = useLocalizedStatic(HOME_SECTIONS);
  const carouselRef = useRef(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromApi, setFromApi] = useState(false);

  useEffect(() => {
    let active = true;

    getBlogs()
      .then((data) => {
        if (!active) return;
        setBlogs((data || []).slice(0, 6).map(mapBlogListItem));
        setFromApi(true);
      })
      .catch((err) => {
        if (!active) return;
        console.warn('Failed to load home blogs:', err?.message || err);
        setBlogs([]);
        setFromApi(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [locale, revision]);

  useLayoutEffect(() => {
    const el = carouselRef.current;
    return () => destroyOwlCarousel(el);
  }, []);

  useLayoutEffect(() => {
    if (loading || !blogs.length) return undefined;
    return initOwlWhenReady(carouselRef.current, OWL_OPTIONS);
  }, [loading, blogs]);

  return (
    <div id="rs-blog" className="rs-blog blog-main-home pt-120 pb-120 md-pt-80 md-pb-80">
      <div className="container">
        <div className="sec-title text-center mb-45 md-mb-25">
          <span className="sub-text">
            <img src={img('shape-1.png')} alt="Images" />
            {copy.blogSub}
          </span>
          <h2 className="title">
            {copy.blogTitleLine1}
            <br />
            {copy.blogTitleLine2}
          </h2>
        </div>

        {loading && (
          <p className="text-center py-4 home-blog-loading">{copy.blogLoading}</p>
        )}

        {!loading && fromApi && blogs.length === 0 && (
          <p className="text-center py-4 home-blog-empty">{copy.blogEmpty}</p>
        )}

        {!loading && !fromApi && (
          <p className="text-center py-4 text-danger home-blog-error">{copy.blogLoadError}</p>
        )}

        {!loading && blogs.length > 0 && (
          <div key={`blogs-${blogs.length}`} className="rs-carousel owl-carousel" ref={carouselRef}>
            {blogs.map((item) => (
              <div key={item.id} className="blog-item">
                <div className="image-wrap">
                  <Link to={item.link}>
                    {item.image ? (
                      <img src={item.image} alt={item.title} />
                    ) : (
                      <div className="blog-item-no-image" aria-hidden="true" />
                    )}
                  </Link>
                </div>
                <div className="blog-content">
                  <ul className="blog-meta">
                    <li className="date">
                      <i className="fa fa-calendar-check-o" />
                      {item.date}
                    </li>
                    <li className="admin">
                      <i className="fa fa-user-o" />
                      {item.author}
                    </li>
                  </ul>
                  <h3 className="blog-title">
                    <Link to={item.link}>{item.title}</Link>
                  </h3>
                  <div className="blog-button">
                    <Link to={item.link}>{copy.readMore}</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeBlogSection;
